import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { normalizeAutocompleteInputValue, wrapAutocompleteInputProps } from '@ui/components/common/TextField/text-input-normalize';
import TextField from '@mui/material/TextField';
import { ErrorWithIcon } from '@ui/components/common/TextField/ErrorWithIcon';
import { ArrowDown2 } from 'iconsax-react';
import { useEventRegistrationTranslation } from '@ui/layout/EventRegistration/hooks/useEventRegistrationTranslation';
import { Box } from '@mui/material';
import { REGISTRATION_FIELD_FILL_BG } from '../registrationFormFieldStyles';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
const filter = createFilterOptions();
const EAutocompleteWithCreate = ({ name, control, label, options: initialOptions, rules, error, bgColor = REGISTRATION_FIELD_FILL_BG, allowCreate = false, onCreateOption, createOptionText = 'Add', fieldType, defaultValue = null, tooltipText, disabled = false, trimWhitespace = true, ...rest }) => {
    const { t } = useEventRegistrationTranslation();
    const [options, setOptions] = useState(initialOptions);
    const controllerDefault = defaultValue ?? null;
    const handleCreateOption = (inputValue) => {
        if (onCreateOption) {
            return onCreateOption(inputValue);
        }
        return {
            label: inputValue,
            value: inputValue.toLowerCase(),
            isNew: true
        };
    };
    useEffect(() => {
        setOptions(initialOptions);
    }, [initialOptions]);
    // Reset options when defaultValue changes to prevent persistence issues
    useEffect(() => {
        setOptions(initialOptions);
    }, [defaultValue, initialOptions]);
    // Create a unique key that includes options and current value to ensure proper re-rendering
    const autocompleteKey = `${name}-${JSON.stringify(initialOptions)}-${defaultValue}`;
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, tooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: tooltipText, size: 'small', variant: 'dark' }) }))] })), [label, tooltipText]);
    return (_jsx(Controller, { name: name, control: control, rules: rules, render: ({ field }) => (_createElement(Autocomplete, { popupIcon: _jsx(ArrowDown2, { size: 16, color: '#454F5B' }), ...rest, key: autocompleteKey, options: options, disablePortal: true, defaultValue: controllerDefault, disabled: disabled, fullWidth: true, freeSolo: allowCreate, selectOnFocus: allowCreate, clearOnBlur: false, getOptionKey: (option) => typeof option === 'string' ? option : String(option.value), getOptionLabel: (option) => {
                if (typeof option === 'string') {
                    return option;
                }
                return option.label;
            }, value: field.value && field.value !== '' && field.value !== null
                ? options.find((opt) => String(opt.value) === String(field.value)) ||
                    // If not found in options, create a temporary option for display
                    (field.value ? { label: field.value, value: field.value, isNew: true } : null)
                : null, onInputChange: allowCreate
                ? (_, inputValue, reason) => {
                    const normalized = trimWhitespace
                        ? normalizeAutocompleteInputValue(inputValue)
                        : inputValue;
                    if (reason === 'input') {
                        field.onChange(normalized);
                    }
                    else if (reason === 'clear') {
                        field.onChange('');
                    }
                }
                : undefined, onChange: (_, value) => {
                if (disabled)
                    return;
                if (value === null) {
                    field.onChange(null);
                    return;
                }
                let selectedValue;
                if (typeof value === 'string') {
                    // Only allow creating new options if allowCreate is true
                    if (allowCreate) {
                        const newOption = handleCreateOption(value);
                        if (!options.find((opt) => opt.value === newOption.value)) {
                            setOptions((prev) => [...prev, newOption]);
                        }
                        selectedValue = newOption.value;
                    }
                    else {
                        // If allowCreate is false, don't create new options from string values
                        return;
                    }
                }
                else if (value.isNew) {
                    // Only allow creating new options if allowCreate is true
                    if (allowCreate) {
                        const newOption = handleCreateOption(value.value);
                        if (!options.find((opt) => opt.value === newOption.value)) {
                            setOptions((prev) => [...prev, newOption]);
                        }
                        selectedValue = newOption.value;
                    }
                    else {
                        // If allowCreate is false, don't create new options
                        return;
                    }
                }
                else {
                    selectedValue = value.value;
                }
                field.onChange(selectedValue);
            }, filterOptions: (options, params) => {
                const inputValue = trimWhitespace
                    ? normalizeAutocompleteInputValue(params.inputValue)
                    : params.inputValue;
                const filtered = filter(options, { ...params, inputValue });
                if (allowCreate && inputValue !== '') {
                    const isExisting = options.some((option) => inputValue.toLowerCase() === option.label.toLowerCase());
                    if (!isExisting) {
                        filtered.push({
                            label: `${createOptionText} "${inputValue}"`,
                            value: inputValue,
                            isNew: true
                        });
                    }
                }
                return filtered;
            }, isOptionEqualToValue: (option, value) => {
                if (typeof option === 'string' || typeof value === 'string') {
                    return option === value;
                }
                return option.value === value.value;
            }, renderOption: (props, option) => {
                const { key, ...optionProps } = props;
                return (_jsxs("li", { ...optionProps, children: [typeof option === 'string' ? option : option.label, typeof option !== 'string' && option.isNew && (_jsxs("span", { style: { fontStyle: 'italic', opacity: 0.7, marginLeft: 8 }, children: ["(", t('eventRegistration.form.new'), ")"] }))] }, key));
            }, renderInput: (params) => (_jsxs(_Fragment, { children: [_jsx(TextField, { ...params, inputProps: wrapAutocompleteInputProps(params.inputProps, trimWhitespace), label: styledLabel, variant: 'filled', onKeyDown: (event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                            }
                        }, sx: {
                            '.MuiFilledInput-root': {
                                backgroundColor: `${bgColor} !important`,
                                borderRadius: '14px !important',
                                width: '100% !important'
                            },
                            '.MuiInputBase-root': {
                                backgroundColor: `${bgColor} !important`,
                                borderRadius: '14px !important',
                                width: '100% !important'
                            }
                        } }), _jsx(ErrorWithIcon, { error: error, mt: 0.5, ml: 0.5 })] })) })) }));
};
export default EAutocompleteWithCreate;
